import { BulkActionDefinition } from '@factorialco/f0-react/dist/experimental'
import {
  Delete,
  Envelope,
  MoneyBag,
  Plus,
  ThumbsDown,
  ThumbsUp,
} from '@factorialco/f0-react/icons/app'
import { Features } from 'generated/featureDefinitions'
import { AttendanceCustomPeriodEmployeeItemFragment } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import { CUSTOM_PERIOD_TYPES } from 'modules/attendance/components/TimeTracking/Layout/form'
import { useAttendanceFeatures } from 'modules/attendance/lib/attendanceFeaturesContext'

interface SelectedEmployeeItem {
  item: AttendanceCustomPeriodEmployeeItemFragment
  checked: boolean
}

interface BulkActionParams {
  itemsStatus: readonly SelectedEmployeeItem[]
  periodType?: string
}

interface BulkActionsReturn {
  primary: BulkActionDefinition[]
  secondary?: BulkActionDefinition[]
}

interface Params {
  isDateNavigationToday: boolean
}

export const useBulkActionsConfig = ({ isDateNavigationToday }: Params) => {
  const { featureAvailability: hasPayroll } = useAttendanceFeatures(Features.payroll_management)

  const getBulkActions = (bulkActionParams: BulkActionParams): BulkActionsReturn => {
    const selectedEmployeeItems =
      bulkActionParams?.itemsStatus
        ?.filter((item) => item.checked === true)
        ?.map((item) => item.item) || []

    const allHavePendingStatus =
      selectedEmployeeItems.length > 0 &&
      selectedEmployeeItems.every((employee) => employee.reviewStatus === 'pending')

    const allHaveApprovedStatus =
      selectedEmployeeItems.length > 0 &&
      selectedEmployeeItems.every((employee) => employee.reviewStatus === 'approved')

    const primaryActions: BulkActionDefinition[] = []
    const secondaryActions: BulkActionDefinition[] = []

    if (allHavePendingStatus) {
      primaryActions.push({
        label: i18n.t('time_tracking.table_collection.bulk_actions.approve'),
        icon: ThumbsUp,
        id: 'approve',
      })
    }

    if (allHaveApprovedStatus) {
      const canCompensatePeriodType =
        bulkActionParams.periodType === CUSTOM_PERIOD_TYPES.monthly ||
        bulkActionParams.periodType === CUSTOM_PERIOD_TYPES.weekly
      const showCompensationButton = canCompensatePeriodType && hasPayroll

      if (showCompensationButton) {
        primaryActions.push({
          label: i18n.t('time_tracking.table_collection.bulk_actions.compensate'),
          icon: MoneyBag,
          id: 'compensate',
        })
      }

      primaryActions.push({
        label: i18n.t('time_tracking.table_collection.bulk_actions.disapprove'),
        icon: ThumbsDown,
        id: 'disapprove',
      })
    }

    if (isDateNavigationToday) {
      primaryActions.push({
        label: i18n.t('time_tracking.table_collection.bulk_actions.send_reminder'),
        icon: Envelope,
        id: 'send-reminder',
      })
    }

    if (selectedEmployeeItems.length > 0) {
      const hasAttendableEmployees = selectedEmployeeItems.some((employee) => employee.attendable)

      const hasNonAttendableEmployees = selectedEmployeeItems.some(
        (employee) => !employee.attendable
      )

      if (hasAttendableEmployees) {
        primaryActions.push({
          label: i18n.t('time_tracking.table_collection.bulk_actions.exclude_from_timesheet'),
          icon: Delete,
          id: 'exclude',
        })
      }

      if (hasNonAttendableEmployees) {
        primaryActions.push({
          label: i18n.t('time_tracking.table_collection.bulk_actions.include_in_timesheet'),
          icon: Plus,
          id: 'include',
        })
      }
    }

    return {
      primary: primaryActions || [],
      secondary: secondaryActions,
    }
  }

  return { getBulkActions }
}
